import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Lock, RefreshCw, Unlock, X } from 'lucide-react';
import MainService from '../../services/MainService';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const AreaStoreNoLock = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAreas = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.AreasInDropDown();
      setAreas(parseList(response));
    } catch (err) {
      console.error('Load area locks error:', err);
      setAreas([]);
      setError('Failed to load areas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const lockArea = async (area) => {
    if (!area?.Id) return;

    try {
      setTogglingId(area.Id);
      setError('');
      const response = await MainService.LockAreaRef(area.Id);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadAreas();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Toggle area ref lock error:', err);
      setError('Network Error');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <Lock className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Area Store No Lock</h1>
              <p className="mt-1 text-sm text-gray-500">Control whether store reference numbers skip already used numbers by area.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadAreas}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th align="right">Options</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                    Loading areas...
                  </td>
                </tr>
              ) : areas.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                    No areas found.
                  </td>
                </tr>
              ) : (
                areas.map((area, index) => (
                  <tr key={area.Id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900">{area.Name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${area.IsLockRef ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'}`}>
                        {area.IsLockRef ? 'Numbers Will Not Skip' : 'Numbers Will Skip'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => lockArea(area)}
                        disabled={togglingId === area.Id}
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                      >
                        {area.IsLockRef ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        {area.IsLockRef ? 'Unlock' : 'Lock'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 ${align === 'right' ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default AreaStoreNoLock;
